'use client';

import { HeroCardBody } from './HeroCardBody';
import { HERO_CARD_LINK_CLASS, HeroCardPhoneLink } from './HeroCardPhoneLink';
import type { HeroCardPostCtaResolved } from './types';
import { Icon } from '@/components/Icon';
import { useRenderContext } from '@/lib/render/RenderContextProvider';

interface HeroCardPostCtaProps {
  postCta: HeroCardPostCtaResolved;
}

/**
 * The block that follows a tab's CTAs: a rich-text line, a phone link and an
 * on-page link, each optional and each rendered only when configured.
 *
 * Shared by both tabs of both tabbed cards, so the four instances cannot drift.
 * The phone and the link are centred and icon-led, matching the legacy site's
 * `.link-phone` and `.link-calendar` under the hero tabs.
 */
export function HeroCardPostCta({ postCta }: HeroCardPostCtaProps) {
  const { substitutePlaceholders: sub } = useRenderContext();

  // The tabbed cards carry no card-level phone pair: this block's own
  // `postCtaPhone` / `postCtaMatelsoPhoneLabel` are the only source, kept as a
  // hard pair (see 70.07-Phone-Number-Resolution) — its own label or none.
  const phone = postCta.phone ? sub(postCta.phone) : '';
  const matelsoPhoneLabel = postCta.matelsoPhoneLabel
    ? sub(postCta.matelsoPhoneLabel)
    : '';
  const phoneLabel = matelsoPhoneLabel || phone;

  const linkLabel = postCta.linkLabel ? sub(postCta.linkLabel) : '';
  const linkAnchor = postCta.linkAnchor ? sub(postCta.linkAnchor) : '';

  // A number with no digits at all (a placeholder an Editor has not filled in
  // yet) would render `tel:` with an empty target, so it is dropped instead.
  const dialable = phone.replace(/[^+\d]/g, '');
  const hasPhone = dialable.length > 0;
  const hasLink = Boolean(linkAnchor);
  if (!postCta.text && !hasPhone && !hasLink) {
    // Nothing configured: render no wrapper, so the panel keeps no trailing gap.
    return null;
  }

  return (
    <div className="flex w-full flex-col gap-2">
      <HeroCardBody nodes={postCta.text} />
      <HeroCardPhoneLink phone={phone} label={phoneLabel} />
      {hasLink && (
        <p className="m-0 text-center">
          {/* An on-page target, so the editor supplies the anchor name only and
              the href is built here — the same shape as the tab anchors. */}
          <a
            href={`#${linkAnchor.replace(/^#/, '')}`}
            className={HERO_CARD_LINK_CLASS}
          >
            <Icon src="/icons/calendar.svg" size={16} className="shrink-0" />
            {linkLabel || linkAnchor}
          </a>
        </p>
      )}
    </div>
  );
}
